/**
 * Edge action registry — single source of truth for the action set
 * surfaced by both the EdgeFloatingToolbar (lock + delete) and the
 * right-click menu's `kind === 'edge'` branch (z-order + lock + delete).
 *
 * Mirrors NODE_ACTIONS / FRAME_ACTIONS in shape. Edge has no copy /
 * duplicate — those aren't supported via the menu today (the floating
 * toolbar doesn't expose them either). The "Add label" affordance on
 * the floating toolbar is intentionally NOT in the registry: it
 * doesn't fit "click → run a handler" — it opens an inline label
 * editor specific to the toolbar.
 */

export interface EdgeActionContext {
  edgeId: string;
  locked: boolean;
  onBringToFront: (id: string) => void;
  onBringForward: (id: string) => void;
  onSendBackward: (id: string) => void;
  onSendToBack: (id: string) => void;
  onToggleLock: (id: string) => void;
  onDelete: (id: string) => void;
}

export interface EdgeAction {
  id:
    | 'bring-to-front'
    | 'bring-forward'
    | 'send-backward'
    | 'send-to-back'
    | 'lock'
    | 'delete';
  label: string;
  icon: string;
  shortcut?: string;
  danger?: boolean;
  labelFor?: (ctx: EdgeActionContext) => string;
  iconFor?: (ctx: EdgeActionContext) => string;
  available: (ctx: EdgeActionContext) => boolean;
  run: (ctx: EdgeActionContext) => void;
}

export const EDGE_ACTIONS: EdgeAction[] = [
  {
    id: 'bring-to-front',
    label: 'Bring to front',
    icon: 'flip_to_front',
    available: (c) => !c.locked,
    run: (c) => c.onBringToFront(c.edgeId),
  },
  {
    id: 'bring-forward',
    label: 'Bring forward',
    icon: 'move_up',
    available: (c) => !c.locked,
    run: (c) => c.onBringForward(c.edgeId),
  },
  {
    id: 'send-backward',
    label: 'Send backward',
    icon: 'move_down',
    available: (c) => !c.locked,
    run: (c) => c.onSendBackward(c.edgeId),
  },
  {
    id: 'send-to-back',
    label: 'Send to back',
    icon: 'flip_to_back',
    available: (c) => !c.locked,
    run: (c) => c.onSendToBack(c.edgeId),
  },
  {
    id: 'lock',
    label: 'Lock',
    icon: 'lock',
    labelFor: (c) => (c.locked ? 'Unlock' : 'Lock'),
    iconFor: (c) => (c.locked ? 'lock_open' : 'lock'),
    available: () => true,
    run: (c) => c.onToggleLock(c.edgeId),
  },
  {
    id: 'delete',
    label: 'Delete',
    icon: 'delete',
    danger: true,
    available: () => true,
    run: (c) => c.onDelete(c.edgeId),
  },
];
